import { PlainNotice } from '@/components/plain-notice';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, CircleDashedIcon, UsersIcon, LayersIcon, BookTextIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { Stat } from '@/components/stat';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusChip } from '@/components/status-chip';
import { organisationOverviewQuery } from '@/lib/api/organisations';
import { useOrgPermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';

/** The nine onboarding steps the API tracks on `organisations.onboardingStep`. */
const ONBOARDING_STEPS = [
  'Create your organisation',
  'Invite your team',
  'Connect your first site',
  'Verify the site domain',
  'Activate a licence',
  'Import existing content',
  'Define your content model',
  'Publish a first change',
  'Set up billing',
] as const;

const TOTAL_STEPS = ONBOARDING_STEPS.length;

function OverviewPage() {
  const { orgId } = Route.useParams();
  const { organisation } = useOrgPermissions(orgId);
  const overview = useQuery(organisationOverviewQuery(orgId));

  const step = overview.data?.onboardingStep ?? organisation?.onboardingStep ?? 0;
  const complete = Math.min(step, TOTAL_STEPS);
  const setupDone = step >= TOTAL_STEPS;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Overview"
        title={organisation?.name ?? 'Your organisation'}
        description="A read on where this workspace stands: who has access, what is connected, and what is left to set up."
        actions={
          <PermissionGate permission={PERMISSIONS.ORG_MEMBERS_INVITE}>
            <Button asChild variant="secondary">
              <Link to="/app/$orgId/team" params={{ orgId }}>
                Invite a teammate
              </Link>
            </Button>
          </PermissionGate>
        }
      />

      <section
        aria-label="Workspace at a glance"
        className="grid gap-8 rounded-lg border border-hairline bg-elevated px-6 py-7 sm:grid-cols-3"
      >
        <Stat
          label="Connected sites"
          value={overview.data?.sites}
          loading={overview.isLoading}
          hint={overview.data?.sites === 0 ? 'None connected yet' : 'Across all environments'}
        />
        <Stat
          label="Team members"
          value={overview.data?.members}
          loading={overview.isLoading}
          hint="Active seats in this organisation"
        />
        <Stat
          label="Setup progress"
          value={overview.isLoading ? undefined : `${complete}/${TOTAL_STEPS}`}
          loading={overview.isLoading}
          tone={setupDone ? 'default' : 'warning'}
          hint={setupDone ? 'Onboarding complete' : 'Steps remaining below'}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {!setupDone ? (
          <Card>
            <CardHeader>
              <span className="section-label">Getting set up</span>
              <CardTitle>Finish connecting SiteFlow</CardTitle>
              <CardDescription>
                Each step unlocks the next part of the product. You can leave and come back — progress
                is stored on the organisation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="flex flex-col">
                {ONBOARDING_STEPS.map((label, index) => {
                  const done = index < complete;
                  const current = index === complete;
                  return (
                    <li
                      key={label}
                      className={cn(
                        'flex items-center gap-3 border-b border-hairline py-2.5 last:border-b-0',
                        !done && !current && 'opacity-55',
                      )}
                    >
                      <span
                        className={cn(
                          'flex size-5 shrink-0 items-center justify-center rounded-full border',
                          done
                            ? 'border-success bg-success text-white'
                            : current
                              ? 'border-accent text-accent'
                              : 'border-hairline-strong text-ink-subtle',
                        )}
                        aria-hidden="true"
                      >
                        {done ? <CheckIcon className="size-3" /> : <CircleDashedIcon className="size-3" />}
                      </span>
                      <span
                        className={cn(
                          'flex-1 text-data',
                          done ? 'text-ink-muted line-through decoration-hairline-strong' : 'text-ink',
                        )}
                      >
                        {label}
                      </span>
                      <span className="font-mono text-micro text-ink-subtle" data-numeric>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      {current ? <StatusChip tone="info">Next</StatusChip> : null}
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <span className="section-label">Recent activity</span>
              <CardTitle>Nothing to report yet</CardTitle>
              <CardDescription>
                Publish and licence events will stream here once the content milestone lands.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Card>
          <CardHeader>
            <span className="section-label">What to do next</span>
            <CardTitle>Suggested actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1">
            <NextAction
              icon={UsersIcon}
              title="Bring the editors in"
              body="Invite the people who own the copy and give them the Editor or Publisher role."
              to="/app/$orgId/team"
              orgId={orgId}
            />
            <NextAction
              icon={LayersIcon}
              title="Connect a site"
              body="Point SiteFlow at a site you already run. Nothing on it changes until you publish."
              to="/app/$orgId/sites"
              orgId={orgId}
            />
            <NextAction
              icon={BookTextIcon}
              title="Review the content model"
              body="Decide which regions of the page editors are allowed to touch."
              to="/app/$orgId/content"
              orgId={orgId}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface NextActionProps {
  icon: typeof UsersIcon;
  title: string;
  body: string;
  to: '/app/$orgId/team' | '/app/$orgId/sites' | '/app/$orgId/content';
  orgId: string;
}

function NextAction({ icon: Icon, title, body, to, orgId }: NextActionProps) {
  return (
    <Link
      to={to}
      params={{ orgId }}
      className={cn(
        'group flex items-start gap-3 rounded-md border border-transparent px-2 py-2.5',
        'transition-colors hover:border-hairline hover:bg-sunken',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
      )}
    >
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-hairline bg-sunken text-ink-subtle group-hover:text-accent">
        <Icon className="size-3.5" />
      </span>
      <span className="min-w-0">
        <span className="block text-data font-medium text-ink">{title}</span>
        <span className="block text-micro leading-relaxed text-ink-muted">{body}</span>
      </span>
    </Link>
  );
}

function NoSiteYet() {
  return (
    <PlainNotice title="Nothing to edit yet">
      <p>No website has been set up for editing. Please ask your administrator to add one.</p>
    </PlainNotice>
  );
}

export const Route = createFileRoute('/app/$orgId/')({
  component: CONTENT_ONLY ? NoSiteYet : OverviewPage,
});
